import { useEffect, useRef } from "react";
import { useAtom } from "jotai";
import { useSnackbar } from "notistack";
import { Button } from "@mui/material";
import { useNavigate } from "react-router-dom";

import { ImportJobResult, ImportJobStatus } from "./import";
import { importAtom } from "./import.state";
import { getContinueHandler, getFinishHandler } from "./import.handlers";
import { trackEvent } from "../analytics/analytics";
import apiClient from "../../services/apiClient";
import { useProducts } from "../products/product-func";
import { useImages } from "../images/image-func";
import { bumpImageVersion } from "../images/image";
import { useCatalogs } from "../catalogs/useCatalogs";
import { useAppState } from "../app/app.service";
import { sessionAtom } from "../accounts/account.state";
import { productDbAtom } from "../products/product.state";
import { devWarn } from "../../services/util";

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 15000;
const counters = ["products", "images", "catalogs"] as const;

/**
 * Session-scoped import poller. Mount once at the app root (inside RouterShell
 * so it has access to SnackbarProvider, sessionAtom, and the data-reload hooks).
 *
 * Polls the active import job (`importAtom.currentJob`) regardless of which page
 * the user is on. When the job finishes, refreshes data DBs, fires the `finish`
 * analytics event, dispatches the consumer's onFinish handler (if registered),
 * and clears the atom. Without this, the finish event was lost whenever the
 * user navigated away from the import page mid-job (Mode C). Renders nothing.
 *
 * Two-component split: this outer component only reads session/productDb to
 * decide whether the user has the data context required by `useProducts`,
 * `useImages`, `useCatalogs`. Without the gate, those hooks crash on
 * unauthenticated routes (productDbAtom resolves to `null` when
 * session.account.config is missing) — see `ImportPollerInner` for the
 * actual polling lifecycle that depends on the resolved data hooks.
 */
export const ImportPoller = () => {
  const [session] = useAtom(sessionAtom);
  const [productDb] = useAtom(productDbAtom);
  if (!session?.account?.config || !productDb) return null;
  return <ImportPollerInner />;
};

// Routes + labels for the generic completion snackbar. `view` is the
// success-path destination (see what was imported); `importPage` is the
// importer route to return to (drill into errors via the status-pill
// dialog, OR import more). Both buttons render whenever their route
// is defined — no longer gated on error presence — because returning
// to the importer is useful in both flows. Concierge commits have no
// dedicated review surface; not in the map.
const SERVICE_ROUTES: Record<
  string,
  {
    view?: string;
    viewLabel?: string;
    importPage?: string;
    importLabel?: string;
  }
> = {
  "shopify-import": {
    view: "/products",
    viewLabel: "View products",
    importPage: "/import/shopify",
    importLabel: "Back to Shopify",
  },
  "woocommerce-import": {
    view: "/products",
    viewLabel: "View products",
    importPage: "/import/woocommerce",
    importLabel: "Back to WooCommerce",
  },
  "products-import": {
    view: "/products",
    viewLabel: "View products",
  },
  "import-pdf": {
    view: "/catalogs",
    viewLabel: "View catalogs",
  },
};


const ImportPollerInner = () => {
  const [importState, setImportState] = useAtom(importAtom);
  const { reloadProductDb } = useProducts();
  const { reloadImageDb } = useImages();
  const { reloadCatalogDb } = useCatalogs();
  const { refreshAccountSummary } = useAppState();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const navigate = useNavigate();

  // Poller reads currentJob via ref to avoid restarting the interval on every poll.
  const stateRef = useRef(importState);
  stateRef.current = importState;

  const currentJob = importState.currentJob;

  useEffect(() => {
    if (!currentJob) return;

    let stopped = false;

    const poll = async () => {
      if (stopped) return;
      try {
        const { data: status } = await apiClient.get<ImportJobStatus>(
          "/data/job-status?jobId=" + currentJob.jobId,
          { timeout: POLL_TIMEOUT_MS },
        );
        if (stopped) return;

        if (status?.job?.finished) {
          await finalizeJob(currentJob, status);
        } else {
          // Update jobStatus in atom so consumer hooks can render progress.
          setImportState((s) => ({ ...s, jobStatus: status }));
          // Notify per-consumer continue handler (used by useProductUpload for progress %)
          const onContinue = getContinueHandler(currentJob.serviceJobId);
          onContinue?.(status);
        }
      } catch (error) {
        // Don't stop polling on transient failures (timeouts, brief 5xx).
        devWarn("Job status poll failed:", error);
      }
    };

    const finalizeJob = async (
      job: typeof currentJob,
      status: ImportJobStatus,
    ) => {
      stopped = true;
      clearInterval(intervalId);

      let result: ImportJobResult = { productIds: [] };
      try {
        const r = await apiClient.get<ImportJobResult>(
          "/data/job-result?jobId=" + job.jobId,
        );
        result = r.data;
      } catch (error) {
        devWarn("Job result fetch failed:", error);
      }

      // Refresh global data caches based on what was imported.
      const counts = status.counters ?? {};
      // An import rewrites product images in place (same path, new bytes), so the session's
      // image token must move or the app keeps showing its cached copy until a page reload.
      // Before the productDb reload, so the re-render already builds the new URLs.
      if ((counts["images"] ?? 0) > 0) bumpImageVersion();
      try {
        if ((counts["products"] ?? 0) > 0) await reloadProductDb();
        if ((counts["images"] ?? 0) > 0) await reloadImageDb();
        if ((counts["catalogs"] ?? 0) > 0) await reloadCatalogDb();
        if (
          (counts["products"] ?? 0) > 0 ||
          (counts["images"] ?? 0) > 0 ||
          (counts["catalogs"] ?? 0) > 0
        ) {
          await refreshAccountSummary();
        }
      } catch (error) {
        devWarn("Post-import refresh failed:", error);
      }

      // Fire the finish analytics event (Mode D — coerce undefined errorCount to 0).
      const counterLabel = counters
        .map((key) => {
          const count = counts[key] ?? 0;
          const total = counts[key + "-total"] ?? 0;
          if (total === 0) return null;
          return `${count} / ${total} ${key}`;
        })
        .filter(Boolean)
        .join(", ");

      trackEvent({
        category: job.serviceJobId,
        action: "finish",
        value: result.productIds.length,
        label: counterLabel + `, errors: ${status.errorCount ?? 0}`,
        // feature:true so completions persist to Analytics.Events (not just
        // the diagnostic trace stream). Pairs with the already-durable `start`
        // event so the LOADED→COMPLETED import funnel is measurable end-to-end.
        // Shared poller, so this lands finish events for every import type
        // (Shopify, CSV, Concierge, PDF) — all low-volume, deliberate actions.
        feature: true,
      });

      // Dispatch the consumer-specific onFinish, if a hook is mounted.
      const onFinish = getFinishHandler(job.serviceJobId);
      try {
        await onFinish?.(status, result);
      } catch (error) {
        devWarn("Consumer onFinish handler threw:", error);
      }

      // Generic completion snackbar — runs even if the consumer hook unmounted
      // (user navigated away mid-import, the previous failure mode for Mode C).
      // Skip when the consumer was mounted: it shows its own contextual snackbar.
      if (!onFinish && counterLabel) {
        const errorCount = status.errorCount ?? 0;
        const errorSuffix = errorCount > 0 ? ` (${errorCount} errors)` : "";
        const routes = SERVICE_ROUTES[job.serviceJobId];
        const variant = errorCount > 0 ? "warning" : "success";
        // Hold the snackbar longer when there are errors — user has more
        // to read and may want to click into the import page for details.
        const autoHide = errorCount > 0 ? 12000 : 6000;
        enqueueSnackbar(`Import complete — ${counterLabel}${errorSuffix}`, {
          variant,
          autoHideDuration: autoHide,
          action:
            routes?.view || routes?.importPage
              ? (snackKey) => (
                  <>
                    {routes?.importPage && (
                      <Button
                        size="small"
                        color="inherit"
                        onClick={() => {
                          navigate(routes.importPage!);
                          closeSnackbar(snackKey);
                        }}
                      >
                        {routes.importLabel ?? "Back to import"}
                      </Button>
                    )}
                    {routes?.view && (
                      <Button
                        size="small"
                        color="inherit"
                        onClick={() => {
                          navigate(routes.view!);
                          closeSnackbar(snackKey);
                        }}
                      >
                        {routes.viewLabel ?? "View"}
                      </Button>
                    )}
                  </>
                )
              : undefined,
        });
      }

      // Clear the active job, capture the last status for "previous import" UI.
      setImportState({
        currentJob: undefined,
        jobStatus: undefined,
        lastJobStatus: status,
        checked: true,
      });
    };

    // Kick off the first poll immediately, then every interval.
    poll();
    const intervalId = setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      stopped = true;
      clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentJob?.jobId]);

  return null;
};
